// Rule-book-sourced budget computation. Canonical enforcement for
// character creation. Keep new constraints here, not sprinkled through UI.
//
// Sources (relative to data/raw/):
// - 03 Characters/Detailed Character Creation/Characteristics.md
// - 03 Characters/Detailed Character Creation/Abilities.md (age cap)
// - 03 Characters/Detailed Character Creation/Abilities/Early Childhood.md
// - 03 Characters/Detailed Character Creation/Abilities/Later Life.md
// - 03 Characters/Detailed Character Creation/Abilities/Magus Only - Apprenticeship.md
// - 03 Characters/Detailed Character Creation/Detailed Character Creation Summary.md
// - 04 Virtues and Flaws/Virtues and Flaws Rules and Guidelines.md
// - 03 Characters/Detailed Character Creation/Confidence.md

import type { Character, CharacterType, ChosenVirtue, ChosenFlaw } from '@/types/character';
import { virtueById, flawById, abilityById, artById, houseBenefits } from '@/data';
import type { Virtue, Flaw } from '@/types/data';
import { abilityScoreFromXp } from '@/lib/scores';

// -------------------- house benefits --------------------
// Virtues granted by a magus's House (Heartbeast for Bjornaer, Puissant Art
// for Flambeau, etc.) are OUTSIDE the 10-point budget per manuscript. This
// table maps house id → map of { virtueId → number of free slots for that id }.
type HouseBenefitRecord = {
  freeVirtues?: Array<{ id: string | null }>;
  freeVirtueChoice?: { options: Array<{ id: string }> };
};

function computeHouseFreeSlots(c: Character): Map<string, number> {
  if (c.type !== 'magus' || !c.magus?.house) return new Map();
  const record = (houseBenefits as { benefits: Record<string, HouseBenefitRecord> })
    .benefits?.[c.magus.house];
  if (!record) return new Map();
  const slots = new Map<string, number>();
  const add = (id: string) => slots.set(id, (slots.get(id) ?? 0) + 1);
  for (const fv of record.freeVirtues ?? []) {
    if (fv.id) add(fv.id);
  }
  if (record.freeVirtueChoice) {
    // The player picks one option. We credit one free slot per *unique* option
    // id so the common case (single-id options differing only by subject) is
    // exact. If the choice spans multiple ids (e.g. Bonisagus puissant-art OR
    // puissant-ability) and the player takes both, this is slightly lenient.
    const ids = new Set(record.freeVirtueChoice.options.map((o) => o.id).filter(Boolean));
    for (const id of ids) add(id);
  }
  return slots;
}

// -------------------- Puissant / Affinity lookups --------------------

function hasMatchingSubjectVirtue(
  c: Character,
  virtueId: string,
  targetName: string
): boolean {
  const target = targetName.trim().toLowerCase();
  if (!target) return false;
  return c.virtues.some(
    (v) => v.id === virtueId && (v.subject ?? '').trim().toLowerCase() === target
  );
}

function puissantArtBonus(c: Character, artId: string): number {
  const def = artById.get(artId);
  if (!def) return 0;
  return hasMatchingSubjectVirtue(c, 'puissant-art', def.name) ? 3 : 0;
}

function puissantAbilityBonus(c: Character, abilityId: string): number {
  const def = abilityById.get(abilityId);
  if (!def) return 0;
  return hasMatchingSubjectVirtue(c, 'puissant-ability', def.name) ? 3 : 0;
}

// Affinity reduces REAL XP cost by 1/3 (equivalently, effective xp = real * 1.5).
// Our `xp` field stores effective / score-equivalent xp, so to compute budget
// consumption we divide by 1.5 (multiply by 2/3) when Affinity is present.
const AFFINITY_FACTOR = 2 / 3;

function artBudgetFactor(c: Character, artId: string): number {
  const def = artById.get(artId);
  if (!def) return 1;
  return hasMatchingSubjectVirtue(c, 'affinity-with-art', def.name) ? AFFINITY_FACTOR : 1;
}

function abilityBudgetFactor(c: Character, abilityId: string): number {
  const def = abilityById.get(abilityId);
  if (!def) return 1;
  return hasMatchingSubjectVirtue(c, 'affinity-with-ability', def.name) ? AFFINITY_FACTOR : 1;
}

// -------------------- virtue / flaw budgets --------------------

export const VIRTUE_BUDGET: Record<CharacterType, number> = {
  grog: 3,
  companion: 10,
  magus: 10,
};

/**
 * Resolve the concrete (category, costPoints) for a picked virtue/flaw.
 * Handles the 'either' category by honouring the player's chosenCategory,
 * falling back to 0 cost + null category when unresolved.
 */
export interface ResolvedPick {
  id: string;
  kind: 'virtue' | 'flaw';
  name: string;
  category: 'major' | 'minor' | 'free' | null;
  costPoints: number;
  subtype: string[] | null;
  needsResolution: boolean;
}

function resolvePick(p: ChosenVirtue | ChosenFlaw, kind: 'virtue' | 'flaw'): ResolvedPick | null {
  const def = (kind === 'virtue' ? virtueById : flawById).get(p.id) as Virtue | Flaw | undefined;
  if (!def) return null;
  let category = def.category;
  let costPoints = def.costPoints ?? 0;
  let needsResolution = false;
  if (category === 'either') {
    if (p.chosenCategory === 'major' || p.chosenCategory === 'minor') {
      category = p.chosenCategory;
      const mag = category === 'major' ? 3 : 1;
      costPoints = kind === 'virtue' ? mag : -mag;
    } else {
      category = null;
      costPoints = 0;
      needsResolution = true;
    }
  }
  return {
    id: p.id,
    kind,
    name: def.name,
    category: category as 'major' | 'minor' | 'free' | null,
    costPoints,
    subtype: def.subtype,
    needsResolution,
  };
}

export interface VirtueFlawStats {
  virtuePoints: number;
  flawPoints: number; // negative
  balanced: boolean;
  budget: number;
  overBudget: boolean;
  minorFlawCount: number;
  majorPersonalityFlaws: number;
  storyFlaws: number;
  personalityFlaws: number;
  socialStatuses: number;
  majorHermeticVirtues: number;
  majorVirtues: number;
  majorFlaws: number;
  hermeticFlaws: number;
  hasHermeticVirtueOrFlaw: boolean;
  hasTheGift: boolean;
  hasHermeticMagusStatus: boolean;
  hasWealthy: boolean;
  hasPoor: boolean;
  unresolvedEither: number;
  resolvedVirtues: ResolvedPick[];
  resolvedFlaws: ResolvedPick[];
}

function hasSubtype(r: ResolvedPick | undefined, needle: string): boolean {
  if (!r || !r.subtype) return false;
  const low = needle.toLowerCase();
  return r.subtype.some((s) => s.toLowerCase() === low || s.toLowerCase().includes(low));
}

export function computeVirtueFlawStats(c: Character): VirtueFlawStats {
  // Allocate house-granted free slots to matching virtues as they're resolved.
  // A free slot overrides the virtue's cost to 0 and category to 'free' for
  // budget purposes. Matches by id in order; a second instance of the same id
  // falls through to its normal cost.
  const freeSlots = computeHouseFreeSlots(c);
  const slotsUsed = new Map<string, number>();
  const resolvedVirtues: ResolvedPick[] = [];
  for (const v of c.virtues) {
    const r = resolvePick(v, 'virtue');
    if (!r) continue;
    const avail = freeSlots.get(v.id) ?? 0;
    const used = slotsUsed.get(v.id) ?? 0;
    if (used < avail) {
      resolvedVirtues.push({ ...r, costPoints: 0, category: 'free' });
      slotsUsed.set(v.id, used + 1);
    } else {
      resolvedVirtues.push(r);
    }
  }
  const resolvedFlaws = c.flaws.map((x) => resolvePick(x, 'flaw')).filter(Boolean) as ResolvedPick[];

  const virtuePoints = resolvedVirtues.reduce((s, v) => s + v.costPoints, 0);
  const flawPoints = resolvedFlaws.reduce((s, f) => s + f.costPoints, 0);
  const budget = VIRTUE_BUDGET[c.type];

  const minorFlawCount = resolvedFlaws.filter((f) => f.category === 'minor').length;
  const majorFlaws = resolvedFlaws.filter((f) => f.category === 'major').length;
  const majorVirtues = resolvedVirtues.filter((v) => v.category === 'major').length;
  const personalityFlaws = resolvedFlaws.filter((f) => hasSubtype(f, 'personality')).length;
  const majorPersonalityFlaws = resolvedFlaws.filter(
    (f) => f.category === 'major' && hasSubtype(f, 'personality')
  ).length;
  const storyFlaws = resolvedFlaws.filter((f) => hasSubtype(f, 'story')).length;
  const socialStatuses =
    resolvedVirtues.filter((v) => hasSubtype(v, 'social status')).length +
    resolvedFlaws.filter((f) => hasSubtype(f, 'social status')).length;

  const majorHermeticVirtues = resolvedVirtues.filter(
    (v) => v.category === 'major' && hasSubtype(v, 'hermetic')
  ).length;
  const hermeticFlaws = resolvedFlaws.filter((f) => hasSubtype(f, 'hermetic')).length;
  const hasHermeticVirtueOrFlaw =
    resolvedVirtues.some((v) => hasSubtype(v, 'hermetic')) ||
    resolvedFlaws.some((f) => hasSubtype(f, 'hermetic'));

  const unresolvedEither =
    resolvedVirtues.filter((v) => v.needsResolution).length +
    resolvedFlaws.filter((f) => f.needsResolution).length;

  return {
    virtuePoints,
    flawPoints,
    balanced: virtuePoints + flawPoints === 0,
    budget,
    overBudget: virtuePoints > budget,
    minorFlawCount,
    majorPersonalityFlaws,
    storyFlaws,
    personalityFlaws,
    socialStatuses,
    majorHermeticVirtues,
    majorVirtues,
    majorFlaws,
    hermeticFlaws,
    hasHermeticVirtueOrFlaw,
    hasTheGift: c.virtues.some((v) => v.id === 'the-gift'),
    hasHermeticMagusStatus: c.virtues.some((v) => v.id === 'hermetic-magus'),
    hasWealthy: c.virtues.some((v) => v.id === 'wealthy'),
    hasPoor: c.flaws.some((f) => f.id === 'poor'),
    unresolvedEither,
    resolvedVirtues,
    resolvedFlaws,
  };
}

// -------------------- warnings --------------------

export interface Warning {
  severity: 'error' | 'warn';
  text: string;
}

export function validate(c: Character): Warning[] {
  const s = computeVirtueFlawStats(c);
  const w: Warning[] = [];

  if (s.unresolvedEither > 0)
    w.push({
      severity: 'error',
      text: `${s.unresolvedEither} "Major or Minor" virtue(s)/flaw(s) need a committed choice`,
    });

  if (s.overBudget)
    w.push({ severity: 'error', text: `Over virtue budget by ${s.virtuePoints - s.budget}` });
  // Manuscript: each character type caps flaw points at the same number as virtues
  // (grog 3, companion 10, magus 10). Balance alone catches most over-cap cases
  // but an explicit check is clearer and defends against unresolved-either edge
  // cases where balance arithmetic understates the real flaw total.
  if (-s.flawPoints > s.budget)
    w.push({
      severity: 'error',
      text: `Over flaw cap by ${-s.flawPoints - s.budget} (max ${s.budget} points)`,
    });
  if (!s.balanced)
    w.push({
      severity: 'error',
      text: `Virtues and Flaws must balance (off by ${s.virtuePoints + s.flawPoints})`,
    });

  if (s.majorPersonalityFlaws > 1)
    w.push({ severity: 'error', text: 'At most 1 Major Personality Flaw' });
  if (s.personalityFlaws > 2)
    w.push({ severity: 'error', text: 'At most 2 Personality Flaws' });
  if (s.storyFlaws > 1) w.push({ severity: 'error', text: 'At most 1 Story Flaw' });

  // Social Status rule is imperative: every character takes exactly one.
  if (s.socialStatuses === 0)
    w.push({ severity: 'error', text: 'Every character must take exactly one Social Status' });
  else if (s.socialStatuses > 1)
    w.push({
      severity: 'error',
      text: `Only one Social Status permitted (you have ${s.socialStatuses})`,
    });

  if (c.type !== 'grog' && s.minorFlawCount > 5)
    w.push({ severity: 'error', text: 'At most 5 Minor Flaws' });

  if (c.type === 'grog') {
    if (s.majorVirtues > 0 || s.majorFlaws > 0)
      w.push({ severity: 'error', text: 'Grogs may not take Major Virtues or Flaws' });
    if (s.hasHermeticVirtueOrFlaw)
      w.push({ severity: 'error', text: 'Grogs may not take Hermetic Virtues or Flaws' });
    if (s.storyFlaws > 0)
      w.push({ severity: 'error', text: 'Grogs may not take Story Flaws' });
    if (s.hasTheGift) w.push({ severity: 'error', text: 'Grogs may not take The Gift' });
    // 3-point flaw floor.
    if (s.flawPoints < -3)
      w.push({ severity: 'error', text: `Grogs cap at 3 flaw points (you have ${-s.flawPoints})` });
  }

  if (c.type === 'companion') {
    // Manuscript: "You may not take Hermetic Virtues and Flaws, unless you have The Gift"
    if (s.hasHermeticVirtueOrFlaw && !s.hasTheGift)
      w.push({
        severity: 'error',
        text: 'Companions cannot take Hermetic Virtues or Flaws without The Gift',
      });
  }

  if (c.type === 'magus') {
    if (!s.hasTheGift) w.push({ severity: 'error', text: 'Magi must have The Gift' });
    if (!s.hasHermeticMagusStatus)
      w.push({ severity: 'error', text: 'Magi must have Hermetic Magus (Social Status)' });
    if (s.hermeticFlaws === 0)
      w.push({ severity: 'warn', text: 'Magi are expected to have at least one Hermetic Flaw' });
    if (s.majorHermeticVirtues > 1)
      w.push({ severity: 'error', text: 'At most 1 Major Hermetic Virtue for magi' });
    // Hard floor: "Characters with lower scores would not be admitted to the Order"
    // Parma Magica 1, Magic Theory 1, Latin 1.
    const parma = c.abilities.find((a) => a.id === 'parma-magica');
    const parmaScore = parma ? abilityScoreFromXp(parma.xp) : 0;
    if (parmaScore < 1)
      w.push({ severity: 'error', text: 'Magi must have Parma Magica 1+' });
    const latin = c.abilities.find(
      (a) => a.id === 'dead-language' && (a.specialization ?? '').toLowerCase() === 'latin'
    );
    const latinScore = latin ? abilityScoreFromXp(latin.xp) : 0;
    if (latinScore < 1)
      w.push({ severity: 'error', text: 'Magi must have Latin 1+' });
    else if (latinScore < 4)
      w.push({ severity: 'warn', text: 'Magi need Latin 4+ to read the books of the Order' });
    const mt = c.abilities.find((a) => a.id === 'magic-theory');
    const mtScore = mt ? abilityScoreFromXp(mt.xp) : 0;
    if (mtScore < 1)
      w.push({ severity: 'error', text: 'Magi must have Magic Theory 1+' });
    else if (mtScore < 3)
      w.push({ severity: 'warn', text: 'Magic Theory <3 means a magus cannot run their own laboratory' });
    const al = c.abilities.find((a) => a.id === 'artes-liberales');
    if (!al) w.push({ severity: 'warn', text: 'Magi typically have Artes Liberales 1+' });
  }

  // Characteristic rules.
  for (const [key, val] of Object.entries(c.characteristics)) {
    if (val > 3 || val < -3)
      w.push({ severity: 'error', text: `${key} out of range (must be -3..+3)` });
  }
  const charSpent = Object.values(c.characteristics).reduce((sum, v) => {
    const abs = Math.abs(v);
    const cost = (abs * (abs + 1)) / 2;
    return sum + (v >= 0 ? cost : -cost);
  }, 0);
  // Improved Characteristics raises the 7-pt budget by 3 per instance.
  const improvedChars = c.virtues.filter((v) => v.id === 'improved-characteristics').length;
  const charBudget = 7 + 3 * improvedChars;
  if (charSpent > charBudget)
    w.push({
      severity: 'error',
      text: `Characteristic budget exceeded (spent ${charSpent} / ${charBudget})`,
    });

  // Age-based Ability score cap (manuscript: under 30 → 5, up to 46+ → 9).
  // If age is unset, skip the cap entirely — it would otherwise default to
  // the strictest value and false-flag builds the player hasn't dated yet.
  if (c.age != null) {
    const cap = ageAbilityCap(c.age);
    for (const a of c.abilities) {
      const score = abilityScoreFromXp(a.xp);
      if (score > cap) {
        const def = abilityById.get(a.id);
        w.push({
          severity: 'warn', // warn because Affinity and similar virtues raise caps
          text: `${def?.name ?? a.id} score ${score} exceeds age cap ${cap} (check cap-raising virtues)`,
        });
      }
    }
  }

  // Multi-pick enforcement per virtue descriptions.
  const multiPick = computeMultiPickViolations(c);
  for (const v of multiPick) w.push(v);

  // Age sanity.
  if (c.age != null && (c.age < 5 || c.age > 120))
    w.push({ severity: 'error', text: `Age ${c.age} is out of range (5–120)` });

  // Supernatural Abilities require a Supernatural Virtue (manuscript:
  // "Academic, Arcane, and Martial Abilities require a Virtue, as do
  // Supernatural Abilities"). Data-driven: every Supernatural-grant Virtue
  // has subtype "supernatural", so we can detect absence reliably.
  const supernaturalAbilities = c.abilities.filter((a) => {
    const def = abilityById.get(a.id);
    return a.xp > 0 && def?.type === 'supernatural';
  });
  if (supernaturalAbilities.length > 0) {
    const hasSupernaturalVirtue = c.virtues.some((v) => hasSubtype(resolvePick(v, 'virtue') ?? undefined, 'supernatural'));
    if (!hasSupernaturalVirtue) {
      const names = supernaturalAbilities
        .map((a) => abilityById.get(a.id)?.name ?? a.id)
        .join(', ');
      w.push({
        severity: 'error',
        text: `Supernatural Abilities (${names}) require a Supernatural Virtue`,
      });
    }
  }

  // Magus spell-level checks.
  if (c.type === 'magus' && c.magus) {
    // Per-spell lab cap: level ≤ Technique + Form + Intelligence + Magic Theory + 3.
    for (const sp of c.magus.spells) {
      if (sp.level == null || sp.levelBand === 'general') continue;
      const cap = spellLevelCap(c, sp.technique, sp.form);
      if (sp.level > cap) {
        w.push({
          severity: 'error',
          text: `${sp.name} (Lv ${sp.level}) exceeds lab cap ${cap} for ${sp.technique}/${sp.form}`,
        });
      }
    }
    // Combined resource cap. Manuscript: apprenticeship gives 240 xp (abilities+arts)
    // AND 120 spell levels — separate pools. Post-apprenticeship gives 30 points
    // per year *shared* between ability-xp, art-xp, and spell-levels. So the check:
    // overflow past the two apprenticeship pools must fit in the post-app pool.
    const age = c.age ?? defaultAge('magus');
    const years = Math.max(0, age - MAGUS_APPRENTICESHIP_AGE_END);
    const postAppPool = years * MAGUS_POST_APPRENTICESHIP_PER_YEAR;
    const levels = c.magus.spells.reduce((sum, sp) => sum + (sp.level ?? 0), 0);
    const nativeLang = (c.nativeLanguage ?? '').trim().toLowerCase();
    // Affinity-adjusted xp: stored xp is score-equivalent; real xp spent is
    // stored × 2/3 when Affinity with that Art/Ability is present.
    const abilityXpNonLang = c.abilities.reduce((sum, a) => {
      const spec = (a.specialization ?? '').trim().toLowerCase();
      const isLangRow = a.id === 'dead-language' || a.id === 'living-language';
      if (isLangRow && nativeLang && spec === nativeLang) return sum;
      return sum + a.xp * abilityBudgetFactor(c, a.id);
    }, 0);
    const artXp = (c.magus.arts ?? []).reduce(
      (sum, a) => sum + a.xp * artBudgetFactor(c, a.id),
      0
    );
    const abilityArtOverflow = Math.max(
      0,
      abilityXpNonLang + artXp - MAGUS_APPRENTICESHIP_XP
    );
    const spellLevelOverflow = Math.max(
      0,
      levels - MAGUS_APPRENTICESHIP_SPELL_LEVELS
    );
    if (abilityArtOverflow + spellLevelOverflow > postAppPool) {
      const over = abilityArtOverflow + spellLevelOverflow - postAppPool;
      w.push({
        severity: 'error',
        text: `Post-apprenticeship budget exceeded by ${over} points — the ${postAppPool} pt/yr is shared between abilities, arts, and spell levels`,
      });
    }
  }

  return w;
}

// -------------------- XP budgets --------------------

export const NATIVE_LANGUAGE_XP = 75;
export const EARLY_CHILDHOOD_XP = 45;
export const LATER_LIFE_XP_PER_YEAR = 15;
export const LATER_LIFE_XP_WEALTHY = 20;
export const LATER_LIFE_XP_POOR = 10;
export const MAGUS_APPRENTICESHIP_XP = 240;
export const MAGUS_APPRENTICESHIP_SPELL_LEVELS = 120;
export const MAGUS_POST_APPRENTICESHIP_PER_YEAR = 30;
export const MAGUS_APPRENTICESHIP_AGE_START = 5;
export const MAGUS_APPRENTICESHIP_AGE_END = 20;

export interface XpBudget {
  /** Native language pool (fixed 75 XP) — siloed from the rest. */
  nativeLanguage: { total: number; spent: number };
  /** Early childhood pool (45 XP among general Abilities; age 0–5). */
  childhood: { total: number };
  /** General later-life XP pool usable for non-Supernatural Abilities. */
  laterLife: { total: number; perYear: number; years: number };
  /** Magus apprenticeship pool: Arts xp + Abilities xp share 240 total. */
  apprenticeship?: { total: number };
  /** Magus post-apprenticeship pool: 30 xp/yr shared among Arts/Abilities/spell levels. */
  postApprenticeship?: { total: number; perYear: number; years: number };
  /** Unified abilities + arts spending across pools above. */
  total: number;
  spent: number;
  remaining: number;
  abilityXp: number;
  artXp: number;
  nativeLanguageXp: number;
  breakdown: Array<{ label: string; amount: number }>;
  spellLevelPool?: { total: number; spent: number; remaining: number };
}

export function computeXpBudget(c: Character): XpBudget {
  const age = c.age ?? defaultAge(c.type);
  const breakdown: Array<{ label: string; amount: number }> = [];
  const stats = computeVirtueFlawStats(c);

  // Native language is its own siloed pool; not rolled into general Ability xp.
  breakdown.push({ label: 'Native language', amount: NATIVE_LANGUAGE_XP });

  // Early childhood: 45 xp among general abilities (under age 5).
  breakdown.push({ label: 'Early childhood abilities', amount: EARLY_CHILDHOOD_XP });

  // Later-life pool. Wealthy = 20/yr, Poor = 10/yr *for companions only*
  // (both virtue/flaw are companion-exclusive per Later Life.md). Other
  // types get the default 15/yr.
  const perYear =
    c.type === 'companion'
      ? stats.hasWealthy
        ? LATER_LIFE_XP_WEALTHY
        : stats.hasPoor
        ? LATER_LIFE_XP_POOR
        : LATER_LIFE_XP_PER_YEAR
      : LATER_LIFE_XP_PER_YEAR;

  let laterYears = 0;
  let laterXp = 0;
  if (c.type !== 'magus') {
    laterYears = Math.max(0, age - MAGUS_APPRENTICESHIP_AGE_START);
    laterXp = laterYears * perYear;
    if (laterXp > 0)
      breakdown.push({ label: `Later life (${laterYears} yr × ${perYear})`, amount: laterXp });
  }

  // Magus pools.
  let apprenticeshipTotal = 0;
  let postAppYears = 0;
  let postAppXp = 0;
  if (c.type === 'magus') {
    apprenticeshipTotal = MAGUS_APPRENTICESHIP_XP;
    breakdown.push({ label: 'Apprenticeship (Arts + Abilities)', amount: apprenticeshipTotal });
    postAppYears = Math.max(0, age - MAGUS_APPRENTICESHIP_AGE_END);
    postAppXp = postAppYears * MAGUS_POST_APPRENTICESHIP_PER_YEAR;
    if (postAppXp > 0)
      breakdown.push({
        label: `Post-apprenticeship (${postAppYears} yr × ${MAGUS_POST_APPRENTICESHIP_PER_YEAR})`,
        amount: postAppXp,
      });
  }

  // Split current spend into (native language) vs (other abilities) vs arts.
  // Match only when the character has explicitly declared a native language
  // and the ability row specializes in that language — otherwise xp leaks
  // into the 75-xp silo unintentionally.
  const nativeLang = (c.nativeLanguage ?? '').trim().toLowerCase();
  let nativeLanguageSpent = 0;
  let abilityXp = 0;
  for (const a of c.abilities) {
    const spec = (a.specialization ?? '').trim().toLowerCase();
    const isLangRow = a.id === 'dead-language' || a.id === 'living-language';
    if (isLangRow && nativeLang && spec && spec === nativeLang) {
      nativeLanguageSpent += a.xp;
    } else {
      abilityXp += a.xp * abilityBudgetFactor(c, a.id);
    }
  }
  const artXp = (c.magus?.arts ?? []).reduce(
    (s, a) => s + a.xp * artBudgetFactor(c, a.id),
    0
  );

  // Non-language pool = childhood + laterLife + apprenticeship + postApp.
  const nonLanguageTotal =
    EARLY_CHILDHOOD_XP + laterXp + apprenticeshipTotal + postAppXp;

  const budget: XpBudget = {
    nativeLanguage: { total: NATIVE_LANGUAGE_XP, spent: nativeLanguageSpent },
    childhood: { total: EARLY_CHILDHOOD_XP },
    laterLife: { total: laterXp, perYear, years: laterYears },
    apprenticeship: c.type === 'magus' ? { total: apprenticeshipTotal } : undefined,
    postApprenticeship:
      c.type === 'magus'
        ? { total: postAppXp, perYear: MAGUS_POST_APPRENTICESHIP_PER_YEAR, years: postAppYears }
        : undefined,
    total: nonLanguageTotal + NATIVE_LANGUAGE_XP,
    spent: abilityXp + artXp + nativeLanguageSpent,
    remaining: nonLanguageTotal - (abilityXp + artXp),
    abilityXp,
    artXp,
    nativeLanguageXp: nativeLanguageSpent,
    breakdown,
  };

  if (c.type === 'magus') {
    const spellLevels = (c.magus?.spells ?? []).reduce((s, sp) => s + (sp.level ?? 0), 0);
    // Post-apprenticeship 30 xp/yr is a single fungible pool: each point can
    // be an Art xp, an Ability xp, OR a spell level. The spell-level pool
    // therefore grows with years after apprenticeship.
    const spellPoolTotal = MAGUS_APPRENTICESHIP_SPELL_LEVELS + postAppXp;
    budget.spellLevelPool = {
      total: spellPoolTotal,
      spent: spellLevels,
      remaining: spellPoolTotal - spellLevels,
    };
  }

  return budget;
}

// -------------------- multi-pick enforcement --------------------
// Manuscript (Virtues and Flaws Rules and Guidelines): "A Virtue or Flaw may
// be taken more than once only if the description explicitly allows it."

interface MultiRule {
  maxTotal?: number; // hard cap on total instances
  distinctSubjects?: boolean; // require each instance have a distinct `subject`
  familyMaxTotal?: { ids: string[]; max: number; label: string }; // combined cap across ids
  label: string; // human-readable virtue name for errors
}

const MULTI_RULES: Record<string, MultiRule> = {
  'puissant-art': { maxTotal: 2, distinctSubjects: true, label: 'Puissant Art' },
  'puissant-ability': { distinctSubjects: true, label: 'Puissant Ability' },
  'affinity-with-art': { maxTotal: 2, distinctSubjects: true, label: 'Affinity with Art' },
  'affinity-with-ability': { distinctSubjects: true, label: 'Affinity with Ability' },
  'great': {
    // Great (Characteristic): up to twice per Characteristic, multiple characteristics allowed.
    // We enforce via family logic at call site (grouped by subject).
    label: 'Great (Characteristic)',
  },
  'minor-magical-focus': {
    familyMaxTotal: {
      ids: ['minor-magical-focus', 'major-magical-focus'],
      max: 1,
      label: 'Magical Focus',
    },
    label: 'Minor Magical Focus',
  },
  'major-magical-focus': {
    familyMaxTotal: {
      ids: ['minor-magical-focus', 'major-magical-focus'],
      max: 1,
      label: 'Magical Focus',
    },
    label: 'Major Magical Focus',
  },
};

function computeMultiPickViolations(c: Character): Warning[] {
  const out: Warning[] = [];
  const byId = new Map<string, ChosenVirtue[]>();
  for (const v of c.virtues) {
    const arr = byId.get(v.id) ?? [];
    arr.push(v);
    byId.set(v.id, arr);
  }

  // Generic "single-pick unless description allows otherwise" — only for ids
  // we know about (data-driven rules set below). We warn rather than error
  // for unknown ids so homebrew isn't blocked.
  for (const [id, picks] of byId) {
    if (picks.length <= 1) continue;
    const rule = MULTI_RULES[id];
    if (!rule) continue;

    if (rule.maxTotal != null && picks.length > rule.maxTotal) {
      out.push({
        severity: 'error',
        text: `${rule.label} taken ${picks.length} times (max ${rule.maxTotal})`,
      });
    }
    if (rule.distinctSubjects) {
      const subjects = picks.map((p) => (p.subject ?? '').trim().toLowerCase());
      const unique = new Set(subjects);
      if (unique.size !== subjects.length || subjects.some((s) => !s)) {
        out.push({
          severity: 'error',
          text: `${rule.label}: each instance needs a distinct subject`,
        });
      }
    }
  }

  // Family caps (e.g. Magical Focus across minor + major + house grants).
  const seenFamilies = new Set<string>();
  for (const [id, rule] of Object.entries(MULTI_RULES)) {
    const fam = rule.familyMaxTotal;
    if (!fam) continue;
    const key = fam.ids.sort().join('|');
    if (seenFamilies.has(key)) continue;
    seenFamilies.add(key);
    const count = fam.ids.reduce((sum, fid) => sum + (byId.get(fid)?.length ?? 0), 0);
    if (count > fam.max) {
      out.push({
        severity: 'error',
        text: `Only ${fam.max} ${fam.label} permitted per character (have ${count})`,
      });
    }
    void id;
  }

  // Great (Characteristic): max 2 per characteristic (subject).
  const greats = byId.get('great') ?? [];
  if (greats.length > 0) {
    const bySubject = new Map<string, number>();
    for (const g of greats) {
      const key = (g.subject ?? '').trim().toLowerCase();
      bySubject.set(key, (bySubject.get(key) ?? 0) + 1);
    }
    for (const [subj, count] of bySubject) {
      if (!subj) {
        out.push({
          severity: 'error',
          text: 'Great (Characteristic): choose which Characteristic',
        });
      } else if (count > 2) {
        out.push({
          severity: 'error',
          text: `Great (${subj}) taken ${count} times (max 2 per Characteristic)`,
        });
      }
    }
  }

  return out;
}

function defaultAge(type: CharacterType): number {
  if (type === 'magus') return 25;
  return 25;
}

// -------------------- magus spell cap --------------------

export function magusStats(c: Character) {
  const intScore = c.characteristics.intelligence;
  const mtAbility = c.abilities.find((a) => a.id === 'magic-theory');
  const mtScore = mtAbility ? abilityScoreFromXp(mtAbility.xp) : 0;
  return { intScore, mtScore };
}

export function spellLevelCap(c: Character, technique: string, form: string): number {
  const techArt = c.magus?.arts.find((a) => a.id === technique);
  const formArt = c.magus?.arts.find((a) => a.id === form);
  // Puissant Art adds +3 to effective Art score for all rolls including lab.
  const techScore =
    (techArt ? artScoreLocal(techArt.xp) : 0) + puissantArtBonus(c, technique);
  const formScore =
    (formArt ? artScoreLocal(formArt.xp) : 0) + puissantArtBonus(c, form);
  const { intScore, mtScore } = magusStats(c);
  // Puissant Magic Theory (via Puissant Ability with subject "Magic Theory") also adds +3.
  const effectiveMt = mtScore + puissantAbilityBonus(c, 'magic-theory');
  return techScore + formScore + intScore + effectiveMt + 3;
}

function artScoreLocal(xp: number): number {
  let s = 0;
  while (((s + 1) * (s + 2)) / 2 <= xp) s++;
  return s;
}

// -------------------- age-based ability cap --------------------

export function ageAbilityCap(age: number | undefined): number {
  if (age == null) return 5;
  if (age < 30) return 5;
  if (age <= 35) return 6;
  if (age <= 40) return 7;
  if (age <= 45) return 8;
  return 9;
}

// Re-export data indexes for component convenience.
export { virtueById, flawById, abilityById, artById };

